import React, { useEffect, useRef, useState } from "react";

/**
 * Embeds the Feingold Property Management Google Reviews widget.
 *
 * The third-party widget script and iframe are deferred until the widget
 * scrolls near the viewport (or after a short idle delay) so they never
 * compete with critical hero/page resources on initial load.
 */
export const GoogleReviewsWidget: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Defer widget activation until near viewport.
  useEffect(() => {
    if (!containerRef.current || visible) return;

    const el = containerRef.current;

    // If IntersectionObserver is available, activate when scrolled near.
    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setVisible(true);
            io.disconnect();
          }
        },
        { rootMargin: "400px" },
      );
      io.observe(el);
      return () => io.disconnect();
    }

    // Fallback: activate after idle.
    const t = setTimeout(() => setVisible(true), 2500);
    return () => clearTimeout(t);
  }, [visible]);

  // Inject the reputation hub widget script once activated.
  useEffect(() => {
    if (!visible) return;
    const scriptId = "lc-review-widget-script";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "text/javascript";
      script.src =
        "https://reputationhub.site/reputation/assets/review-widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, [visible]);

  return (
    <div ref={containerRef} className="w-full">
      {visible ? (
        <iframe
          className="lc_reviews_widget"
          src="https://reputationhub.site/reputation/widgets/review_widget/mT80PbegrbDSOV5X6nOi"
          frameBorder={0}
          scrolling="no"
          style={{ minWidth: "100%", width: "100%" }}
          title="Feingold Property Management Google Reviews"
          loading="lazy"
        />
      ) : (
        <div
          className="w-full min-h-[320px] rounded-2xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 animate-pulse flex items-center justify-center"
          aria-hidden="true"
        >
          <span className="text-sm text-slate-600 dark:text-slate-300">
            Loading reviews…
          </span>
        </div>
      )}
    </div>
  );
};
